import { inject, Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import {
  BasicDialogService,
  confirmationDialogData,
  DialogPresets,
} from '@principle-theorem/ng-shared';
import {
  BridgeDeviceDownloadStatus,
  type IBridgeDevice,
  type IGenerateDownloadRequest,
  type IGenerateIntegrationKeyRequest,
  type IGenerateIntegrationKeyResponse,
  type IPractice,
} from '@principle-theorem/principle-core/interfaces';
import {
  type DocumentReference,
  type SerialisedData,
  deleteDoc,
  httpsCallable,
  patchDoc,
  serialise,
  snapshot,
} from '@principle-theorem/shared';
import {
  DownloadBridgeProgressDialogComponent,
  type IDownloadBridgeProgressDialogData,
} from '../components/download-bridge-progress-dialog/download-bridge-progress-dialog.component';
import {
  IntegrationKeyDialogComponent,
  type IIntegrationKeyDialogData,
} from '../components/integration-key-dialog/integration-key-dialog.component';

export interface IBridgeDeviceActionContext {
  deviceRef: DocumentReference<IBridgeDevice>;
  deviceName: string;
  deviceId: string;
  hasIntegrationKey: boolean;
  practiceRef: DocumentReference<IPractice>;
}

@Injectable({ providedIn: 'root' })
export class BridgeDeviceActionsService {
  private _dialog = inject(MatDialog);
  private _basicDialog = inject(BasicDialogService);

  async generateIntegrationKey(
    context: IBridgeDeviceActionContext
  ): Promise<boolean> {
    if (context.hasIntegrationKey) {
      const confirmed = await this._basicDialog.confirm(
        confirmationDialogData({
          title: 'Regenerate Integration Key',
          prompt: [
            'Are you sure you want to generate a new integration key?',
            'If you are using the existing integration key, the device will no longer be able to connect.',
          ],
          submitLabel: 'Regenerate Integration Key',
          submitColor: 'warn',
        })
      );
      if (!confirmed) {
        return false;
      }
    }

    const data = serialise<IGenerateIntegrationKeyRequest>({
      deviceRef: context.deviceRef,
      practiceRef: context.practiceRef,
    });
    const response = await snapshot(
      httpsCallable<
        SerialisedData<IGenerateIntegrationKeyRequest>,
        IGenerateIntegrationKeyResponse
      >('http-bridgeDevice-generateKey')(data)
    );

    this._dialog.open<IntegrationKeyDialogComponent, IIntegrationKeyDialogData>(
      IntegrationKeyDialogComponent,
      DialogPresets.medium({
        data: { integrationKey: response.integrationKey },
      })
    );
    return true;
  }

  async generateDownloadLink(
    context: IBridgeDeviceActionContext
  ): Promise<boolean> {
    if (!context.deviceId) {
      return false;
    }

    if (context.hasIntegrationKey) {
      const confirmed = await this._basicDialog.confirm(
        confirmationDialogData({
          title: 'Bridge Device Already Integrated',
          prompt: [
            'This device already has an integration key. Generating a new download link will void the current key and require re-installation.',
            'Proceed with link generation?',
          ],
          submitLabel: 'Generate Download Link',
        })
      );
      if (!confirmed) {
        return false;
      }
    }

    const data = serialise<IGenerateDownloadRequest>({
      deviceRef: context.deviceRef,
      practiceRef: context.practiceRef,
      deviceId: context.deviceId,
    });

    await patchDoc(context.deviceRef, {
      downloadStatus: BridgeDeviceDownloadStatus.Initialising,
    });

    const dialogRef = this._dialog.open<
      DownloadBridgeProgressDialogComponent,
      IDownloadBridgeProgressDialogData
    >(
      DownloadBridgeProgressDialogComponent,
      DialogPresets.small({
        data: { deviceRef: context.deviceRef },
        disableClose: true,
      })
    );

    snapshot(
      httpsCallable<SerialisedData<IGenerateDownloadRequest>>(
        'http-bridgeDevice-generateDownload'
      )(data)
    ).catch(async () => {
      await patchDoc(context.deviceRef, {
        downloadStatus: BridgeDeviceDownloadStatus.Failed,
      });
      dialogRef.close();
    });

    return true;
  }

  async deleteDevice(
    deviceRef: DocumentReference<IBridgeDevice>,
    deviceName: string
  ): Promise<boolean> {
    const confirmed = await this._basicDialog.confirm(
      confirmationDialogData({
        title: 'Delete Bridge Device',
        prompt: [
          `Are you sure you want to delete "${deviceName}"?`,
          'This action cannot be undone.',
        ],
        submitLabel: 'Delete',
        submitColor: 'warn',
      })
    );

    if (!confirmed) {
      return false;
    }

    await deleteDoc(deviceRef);
    return true;
  }
}
